import { useCallback, useLayoutEffect, useMemo, useRef } from "react";

/**
 * Identifies each opening of a form, so a mutation that resolves later can tell whether the form
 * that submitted it is still the one on screen.
 *
 * `sessionKey` is whatever identifies the open form: pass a falsy value when it is closed, and a
 * new value whenever it should count as a fresh start. Changing the key ends one session and
 * begins another, which is how a form that stays mounted but switches to a different subject
 * stops accepting the previous subject's results.
 *
 * Call `recordSubmission()` as the request goes out and `isStale()` when it resolves. Stale means
 * the submitting session has ended — closed, re-keyed, or unmounted — so its result has nowhere to
 * render, and showing it inline would either write to an unmounted component or blame a later
 * attempt for it.
 *
 * Two deliberate choices:
 *
 * - The id lives in refs, not state. Deriving it from render scope would make correctness depend
 *   on the mutation callbacks being re-created on every render, and a later `useCallback` around
 *   them would silently judge every session stale.
 * - `useLayoutEffect`, not `useEffect`, so the cleanup runs during the commit. React 17 flushes
 *   passive cleanup asynchronously, including on unmount, which would otherwise leave a window
 *   where an unmounted form still reads as open — the one direction that is not fail-safe. This is
 *   defensive only: driving the app in a browser, no route reached that state. Neither closing the
 *   settings overlay nor switching its side-nav section ends the create dialog's session, so the
 *   result took the fresh path both times. Keep it for the cost of one word, but do not assume it
 *   is preventing an observed bug.
 *
 * There is one submission slot, so callers must disable submit while a request is in flight.
 * Without that, two submissions from different sessions share the slot and the earlier one's
 * result reads as fresh.
 */
export function useFormSession(sessionKey: Maybe<string | boolean>) {
  const sessionCounterRef = useRef(0);
  const openSessionIdRef = useRef<Nullable<number>>(null);
  const submittedSessionIdRef = useRef<Nullable<number>>(null);

  useLayoutEffect(() => {
    if (!sessionKey) return;

    openSessionIdRef.current = ++sessionCounterRef.current;

    // Runs when the form closes, when the key changes, and when the component unmounts — the last
    // being something no piece of render-scope state can report.
    return () => {
      openSessionIdRef.current = null;
    };
  }, [sessionKey]);

  const recordSubmission = useCallback(() => {
    submittedSessionIdRef.current = openSessionIdRef.current;
  }, []);

  const isStale = useCallback(
    () =>
      // The null check is not redundant with the comparison: when nothing has been submitted both
      // refs are null, and `null !== null` would report a result belonging to no session as fresh.
      openSessionIdRef.current === null ||
      openSessionIdRef.current !== submittedSessionIdRef.current,
    []
  );

  return useMemo(() => ({ isStale, recordSubmission }), [isStale, recordSubmission]);
}
